import {combine, createEffect, createEvent, createStore} from 'effector';

import {authorize, authorizeWithCert, deauthorize, fetchProfile} from 'api';
import {createMfaSession, verifyMfaSession} from 'api/password';
import {createOrganization} from 'api/registration';
import {PERMISSION_ALIASES, PermissionAliasMap} from 'core/constants/permissionAliases';
import {ROLES_MAP} from 'core/constants/roleMatrix';
import {TokenStorage} from 'core/tokenStorage';
import {AuthData, Profile, UserPermission, UserPermissionValue, UserRoles} from 'types';
import {CertData} from 'types/plugins/cadesPlugin';

import {ProfileRole} from 'types/profile';
import {findPermissions} from 'utils/roles';

export type PermissionsState = UserPermissionValue[] &
    Record<string, UserPermissionValue> &
    PermissionAliasMap;

const createPermissionsState = (permissions: UserPermissionValue[]): PermissionsState => {
    const permissionList = Array.from(new Set(permissions)) as PermissionsState;

    Object.entries(PERMISSION_ALIASES).forEach(([alias, permissionValue]) => {
        permissionList[alias as keyof PermissionAliasMap] = permissionValue;
    });

    permissionList.forEach((permission) => {
        permissionList[permission] = permission;

        if (permission.startsWith('PERMISSION_')) {
            permissionList[permission.replace(/^PERMISSION_/, '')] = permission;
        }
    });

    return permissionList;
};

export const loadProfileFx = createEffect({
    name: 'loadSessionUserFx',
    handler: fetchProfile,
});
export const loginFx = createEffect({
    name: 'loginFx',
    handler: authorize,
});
export const loginWithCertFx = createEffect({
    name: 'loginWithCertFx',
    handler: authorizeWithCert,
});
export const logoutFx = createEffect({
    name: 'logoutFx',
    handler: deauthorize,
});
export const cleanAuthDataFx = createEffect({
    name: 'cleanAuthDataFx',
    handler: () => TokenStorage.removeTokens(),
});

export const createOrganizationFx = createEffect({
    name: 'createOrganizationFx',
    handler: createOrganization,
});

export const reloadPageFx = createEffect(() => window.location.reload());

export const doLogin = createEvent<{login: string; password: string; saveCredentials: boolean}>();
export const doLoginWithCert = createEvent<{cert: CertData; id: number}>();
export const doLogout = createEvent<boolean>();
export const cleanAuthData = createEvent();

export const profile$ = createStore<Profile | null>(null);
export const isAuth$ = profile$.map((user) => !!user);
export const isOperator$ = profile$.map((user) =>
    user?.permissions.includes(UserPermission.PERMISSION_MPL_OPERATOR_ADDITIONAL_TABLES_DATA),
);
export const isDirector$ = profile$.map((user) =>
    user?.roles.every((role) =>
        [
            UserRoles.ROLE_MPL_DISTRIBUTOR_DIRECTOR,
            UserRoles.ROLE_MPL_MANUFACTURER_DIRECTOR,
            UserRoles.ROLE_MPL_IMPORTER_DIRECTOR,
            UserRoles.ROLE_MPL_RETAIL_DIRECTOR,
            UserRoles.ROLE_MPL_TS_DIRECTOR,
        ].includes(role),
    ),
);
export const isAdmin$ = profile$.map((user) =>
    user?.roles.includes(UserRoles.ROLE_MPL_OPERATOR_ADMIN),
);

export const isCustomsRole$ = profile$.map((user) =>
    user?.roles.some((role) =>
        [UserRoles.ROLE_MPL_TS_DIRECTOR, UserRoles.ROLE_MPL_TS_EMPLOYEE].includes(role),
    ),
);

export const isCustomsDirector$ = profile$.map((user) =>
    user?.roles.includes(UserRoles.ROLE_MPL_TS_DIRECTOR),
);

export const localOrImporterType$ = profile$.map((user) => {
    const types = user?.roles.length ? user.roles.map((role) => ROLES_MAP[role]) : [];

    if (types.includes(ProfileRole.MANUFACTURER) && types.includes(ProfileRole.IMPORTER))
        return 'both';
    if (types.includes(ProfileRole.MANUFACTURER)) return 'local';
    if (types.includes(ProfileRole.IMPORTER)) return 'import';
    return null;
});

export const permissions$ = profile$.map((user) => {
    if (!user) return createPermissionsState([]);

    const rolePermissions = user.roles.map((role) => findPermissions(role) || []);

    return createPermissionsState([...user.permissions, ...rolePermissions.flat()]);
});

export const $userRoles = profile$.map((user) => user?.roles);

export const profileState$ = combine({
    profile: profile$,
    loading: loadProfileFx.pending,
    isDirector: isDirector$,
});

export const authSyncReceived = createEvent<'LOGIN' | 'LOGOUT'>();

export const loginError$ = createStore<string | null>(null);

export const loginRedirectFromPrevPath$ = createStore<boolean>(true);

export const verifyOtpFx = createEffect({
    name: 'verifyOtpFx',
    handler: verifyMfaSession,
});

export const sendOtpFx = createEffect({
    name: 'sendOtpFx',
    handler: createMfaSession,
});

export const resetAuthData = createEvent();

export const tempAuthData$ = createStore<{authData: AuthData; saveCredentials: boolean} | null>(
    null,
);
export const otpError$ = createStore<boolean>(false);

export const otpState$ = combine({
    tempData: tempAuthData$,
    error: otpError$,
    pending: verifyOtpFx.pending,
    sending: sendOtpFx.pending,
});

export const tempEmail$ = tempAuthData$.map((data) => data?.authData.profile.email || '');
